import React from "react"
import { Link } from "gatsby"
import { Dropdown } from "react-bootstrap"
import { defaultLanguage, langPrefixes } from "../../prismic-configuration"

// Language display names
const languageNames = {
    "en-gb": "English",
    "ar-ae": "العربية",
}

// Language flags/icons (using emoji for simplicity, can be replaced with images)
const languageFlags = {
    "en-gb": "🇬🇧",
    "ar-ae": "🇦🇪",
}

/**
 * Get the URL for a specific language version of the current page
 * @param {object} activeDocMeta - Current document metadata with alternateLanguages
 * @param {string} targetLang - Target language code
 * @returns {string} URL for the target language version
 */
const getLanguageUrl = (activeDocMeta, targetLang) => {
    const { type, alternateLanguages = [] } = activeDocMeta || {}

    // If switching to default language (English)
    if (targetLang === defaultLanguage) {
        // Find the English version in alternates or use root
        const englishVersion = alternateLanguages.find(
            alt => alt.lang === defaultLanguage
        )
        if (englishVersion) {
            // For homepage, return root
            if (type === "homepage") return "/"
            if (type === "blog") return `/blogs/${englishVersion.uid}`
            return `/${englishVersion.uid}`
        }
        return "/"
    }

    // Find the target language version in alternates
    const targetVersion = alternateLanguages.find(alt => alt.lang === targetLang)
    const prefix = langPrefixes[targetLang] || targetLang.split("-")[0]

    if (targetVersion) {
        if (type === "homepage") return `/${prefix}`
        if (type === "blog") return `/${prefix}/blogs/${targetVersion.uid}`
        return `/${prefix}/${targetVersion.uid}`
    }

    // Fallback to language homepage if no alternate found
    return `/${prefix}`
}

export const LanguageSwitcher = ({ activeDocMeta }) => {
    const currentLang = activeDocMeta?.lang || defaultLanguage
    const currentLangName = languageNames[currentLang] || currentLang
    const currentFlag = languageFlags[currentLang] || "🌐"

    // Get all available languages from config
    const availableLanguages = Object.keys(langPrefixes)

    return (
        <Dropdown className="language-switcher ms-2">
            <Dropdown.Toggle
                variant="outline-secondary"
                size="sm"
                id="language-dropdown"
                className="language-switcher-toggle"
            >
                <span className="language-flag me-1">{currentFlag}</span>
                <span className="language-name d-none d-md-inline">{currentLangName}</span>
            </Dropdown.Toggle>

            <Dropdown.Menu className="language-switcher-menu">
                {availableLanguages.map(lang => {
                    const isActive = lang === currentLang
                    const url = getLanguageUrl(activeDocMeta, lang)

                    return (
                        <Dropdown.Item
                            key={lang}
                            as={Link}
                            to={url}
                            className={`language-option ${isActive ? "active" : ""}`}
                        >
                            <span className="language-flag me-2">{languageFlags[lang]}</span>
                            <span className="language-name">{languageNames[lang]}</span>
                        </Dropdown.Item>
                    )
                })}
            </Dropdown.Menu>
        </Dropdown>
    )
}

export default LanguageSwitcher
